import type { Dispatch, SetStateAction } from 'react';
import type { LineItemField } from './InvoiceLineItemsSection';
import { focusOpenPasteCellEditor } from './useLineItemGridCellKeyboardNav';

/** Matches `cellKey` pattern in `invoiceDetailRenderCells` (`${prefix}-${row}-${priceField}`). */
export type InvoicePartGridNumericKeyPrefix = 'lineItem' | 'freightItem' | 'restockingFeeItem' | 'ignoredPart';

export function createInvoicePartPasteCellReplaceKeyHandler(options: {
    getContainer: () => HTMLElement | null;
    pasteColFields: readonly LineItemField[];
    updateCell: (row: number, field: LineItemField, value: unknown) => void;
    numericKeyPrefix: InvoicePartGridNumericKeyPrefix;
    setEditingNumericField?: (v: string | null) => void;
    setEditingNumericValues?: Dispatch<SetStateAction<Record<string, string>>>;
}): (row: number, col: number, key: string) => void {
    const { getContainer, pasteColFields, updateCell, numericKeyPrefix, setEditingNumericField, setEditingNumericValues } = options;

    return (row: number, col: number, key: string) => {
        const field = pasteColFields[col];
        if (!field) return;
        const host = getContainer();
        if (!host) return;

        if (col >= 3) {
            if (field !== 'listPrice' && field !== 'unitBuyPrice' && field !== 'totalBuyPrice') return;
            if (!setEditingNumericField || !setEditingNumericValues) return;
            const cellKey = `${numericKeyPrefix}-${row}-${field}`;
            const allowMinus = field === 'totalBuyPrice';
            if (key === 'Backspace' || key === 'Delete') {
                setEditingNumericField(cellKey);
                setEditingNumericValues((p) => ({ ...p, [cellKey]: '' }));
            } else if (key.length === 1 && (allowMinus ? /^[-\d.]$/ : /^[\d.]$/).test(key)) {
                setEditingNumericField(cellKey);
                setEditingNumericValues((p) => ({ ...p, [cellKey]: key }));
            } else {
                return;
            }
            focusOpenPasteCellEditor(host, row, col);
            return;
        }

        if (key === 'Backspace' || key === 'Delete') {
            if (field === 'qty') updateCell(row, 'qty', 0);
            else if (field === 'title' || field === 'partNumber') updateCell(row, field, '');
            else return;
        } else if (key.length === 1) {
            if (field === 'qty') {
                const d = parseInt(key, 10);
                if (!Number.isFinite(d)) return;
                updateCell(row, 'qty', d);
            } else if (field === 'title' || field === 'partNumber') {
                updateCell(row, field, key);
            } else return;
        } else {
            return;
        }
        focusOpenPasteCellEditor(host, row, col);
    };
}
